import { createSlice, Dispatch, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'
import { RootState } from 'redux/store'

import { Frequency } from 'models/RecurringDonationPlan'
import { CartsResponse, ReducedCartData, ReloadCartArgs } from 'models/Carts'

import cartsApi from 'redux/services/carts'
import { extractReducedCartData } from 'utils/functions/extractReducedCartData'

const initialState: ReducedCartData = {
  id: '',
  organizationId: '',
  campaignId: '',
  amount: '',
  currency: '',
  items: undefined,
  loading: false,
}

export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    updateCart: (state, action: PayloadAction<Partial<ReducedCartData>>) => {
      // If the cart contains only the items and do not contain the amount, we update the amount from the items
      if (action.payload.items && !action.payload.amount) {
        const amount = action.payload.items.reduce(
          (acc, item) => acc + item.unitPrice * item.qty,
          0,
        )

        return { ...state, ...action.payload, amount: String(amount) }
      }

      return { ...state, ...action.payload }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(reloadCart.pending, (state) => {
        // Handle loading state (e.g., set a loading flag)
        state.loading = true
      })
      .addCase(reloadCart.fulfilled, (state, action: PayloadAction<ReducedCartData>) => {
        // Update state with the fetched data
        return { ...state, ...action.payload, loading: false }
      })
      .addCase(reloadCart.rejected, (state, action) => {
        // Handle error state
        state.loading = false
        console.error('Failed to reload cart:', action.error.message)
      })
  },
})

export const getFrequencies =
  () =>
  (_dispatch: Dispatch, getState: () => RootState): Frequency[] => {
    const cart = getState().order.cart

    return cart.items?.map((item) => item.frequency as Frequency) || []
  }

export const reloadCart = createAsyncThunk<ReducedCartData, ReloadCartArgs>(
  'cart/reloadCart',
  async (args, { rejectWithValue, dispatch }) => {
    const { cart_id } = args

    const cartResponse = await dispatch(
      cartsApi.endpoints.getCart.initiate(cart_id as string),
    ).unwrap()

    if (cartResponse?.errors) {
      // Concatenate every error message into a single string
      const errorMessage = cartResponse.errors.map((error) => error.detail).join('. ')

      return rejectWithValue(errorMessage)
    }

    return extractReducedCartData(cartResponse as CartsResponse)
  },
)

export const { updateCart } = cartSlice.actions

export default cartSlice.reducer
